import { useReactFlow } from '@xyflow/react';
import { memo, ReactNode, useCallback, useState } from 'react';
import { MenuTrigger } from 'react-aria-components';

import { Button, Flex, Menu, MenuItem, MenuItemDelete, Popover, Save, Text } from '@/components/core';
import { ExportOption, NewExportModal } from '@/components/flow';
import { AddIcon, OverflowIcon } from '@/components/icons';
import { useAddExport, useDeleteExport } from '@/hooks';

export type ExportHeaderProps = {
  agentId: string;
  actionId: string;
  children?: ReactNode;
};

const IExportHeader = ({ agentId, actionId, children }: ExportHeaderProps) => {
  const [newExportModelOpened, setNewExportModalOpened] = useState(false);
  const builder = useReactFlow();

  const onAddExportSuccess = useCallback(() => {
    builder.setNodes((nodes) => nodes);
    setNewExportModalOpened(false);
  }, [builder]);

  const {
    addExport,
    isPending: isAddingExport,
    errors: addExportErrors,
  } = useAddExport({ onSuccess: onAddExportSuccess });

  const { deleteExport, isPending: isDeletingExport } = useDeleteExport();

  const onDeleteExport = useCallback(() => {
    deleteExport({ agentId, actionId }).then(() => {
      builder.setNodes((nodes) => nodes);
    });
  }, [deleteExport, agentId, actionId, builder]);

  const onExportOptionSelected = useCallback(
    (exportOption: ExportOption) => {
      addExport({ agentId, exportOption }).catch(console.error);
    },
    [addExport, agentId]
  );

  // TODO: Figure out why uncontrolled doesn't work here
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Flex className="w-full">
      <Flex className="gap-sm grow items-center">{children}</Flex>
      {isDeletingExport ? (
        <Save message="Deleting" />
      ) : (
        <MenuTrigger>
          <Button variant="plain" onPress={() => setIsOpen(true)}>
            <OverflowIcon />
          </Button>
          <Popover isOpen={isOpen} onOpenChange={setIsOpen}>
            <Menu>
              <MenuItemDelete onAction={onDeleteExport} />
              <MenuItem
                onAction={() => {
                  setNewExportModalOpened(true);
                  setIsOpen(false);
                }}
              >
                <AddIcon />
                <Text>New export</Text>
              </MenuItem>
            </Menu>
          </Popover>
        </MenuTrigger>
      )}
      <NewExportModal
        isAddingExport={isAddingExport}
        isOpen={newExportModelOpened}
        onExportOptionSelected={onExportOptionSelected}
        onOpenChange={() => setNewExportModalOpened(false)}
        errors={addExportErrors}
      />
    </Flex>
  );
};

export const ExportHeader = memo(IExportHeader) as typeof IExportHeader;
